import { Component, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'app-terms',
  template: `
    <div class="terms-container">
      <div class="terms-card">
        <h1>Terms and Conditions</h1>
        <p>Last Updated: <strong>April 13, 2025</strong></p>

        <section class="section" id="introduction">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Litmus Learn and Hey Lumos – products and brands of
            Dhrona EdTech Private Ltd (“Company”, “we”, “us”). By accessing or
            using our services, including our website, mobile applications, or
            any related products, you (“User”) agree to be bound by these Terms
            and Conditions and our Privacy Policy. If you do not agree, please
            do not use our services.
          </p>
        </section>

        <section class="section" id="definitions">
          <h2>2. Definitions</h2>
          <p>For the purposes of these Terms:</p>
          <ul>
            <li>
              <strong>“Litmus Learn Materials”</strong> means all content
              provided by the Company including websites, mobile applications,
              lesson materials, quizzes and other digital content.
            </li>
            <li>
              <strong>“User”</strong> means anyone accessing or using the Litmus
              Learn Materials, including institutions, school administrators,
              teachers, students, and, where applicable, their parents or legal
              guardians.
            </li>
            <li>
              <strong>“Institution”</strong> means a school or educational
              institution that has entered into a contractual agreement with the
              Company to use our services.
            </li>
          </ul>
        </section>

        <section class="section" id="account-security">
          <h2>3. Account, Security and Payments</h2>
          <p>
            To use our services, you may be required to create an account. You
            agree to provide accurate, complete, and updated information. You
            are responsible for safeguarding your account details and for all
            activities that occur under your account.
          </p>
          <p>In cases where services are provided to institutions:</p>
          <ul>
            <li>
              Institutions will purchase user licenses on a per-student,
              per-month basis.
            </li>
            <li>
              Payment must be made in advance and failure to pay may result in
              service suspension.
            </li>
          </ul>
        </section>

        <section class="section" id="usage-rights">
          <h2>4. Use of the Litmus Learn Materials and Services</h2>
          <p>
            Subject to your compliance with these Terms, we grant you a limited,
            non-exclusive, revocable license to access and use our Litmus Learn
            Materials strictly for personal or educational, non-commercial use.
          </p>
          <p>You agree not to:</p>
          <ul>
            <li>
              Copy, reproduce, modify, distribute, or create derivative works
              from our Materials except as expressly permitted.
            </li>
            <li>Use our services for any illegal or unauthorized purpose.</li>
            <li>
              Reverse engineer, decompile, or attempt to extract the source code
              of our software.
            </li>
          </ul>
        </section>

        <section class="section" id="intellectual-property">
          <h2>5. Intellectual Property Rights</h2>
          <p>
            All Litmus Learn Materials and related content are either owned by
            or licensed to Dhrona EdTech Private Ltd. You may not use any
            trademarks, logos, or copyrighted materials displayed on our
            services without obtaining our express written consent.
          </p>
          <p>
            You retain all rights in any content you generate (“User-Generated
            Content”), but by submitting such content to our platform you grant
            us a perpetual, irrevocable, worldwide, royalty-free license to use,
            reproduce, modify, publish, and distribute your content for the
            purpose of delivering our services.
          </p>
        </section>

        <section class="section" id="disclaimers">
          <h2>6. Disclaimer of Warranties and Limitation of Liability</h2>
          <p>
            Our services are provided on an “as is” and “as available” basis. We
            expressly disclaim all warranties, whether express, implied, or
            statutory, including but not limited to the implied warranties of
            merchantability, fitness for a particular purpose, and
            non-infringement.
          </p>
          <p>
            In no event shall Dhrona EdTech Private Ltd or its affiliates be
            liable for any indirect, incidental, consequential, special or
            punitive damages or loss of profits arising out of or in connection
            with your use of the services.
          </p>
        </section>

        <section class="section" id="indemnification">
          <h2>7. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Dhrona EdTech
            Private Ltd and its affiliates from any claims, damages,
            liabilities, and costs (including legal fees) arising from your
            breach of these Terms or your use of our services.
          </p>
        </section>

        <section class="section" id="governing-law">
          <h2>8. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of India. Any disputes arising under these Terms shall
            first be attempted to be resolved through mediation. If unresolved,
            disputes will be settled by arbitration in Chennai, Tamil Nadu,
            India, and the arbitrator’s decision shall be final and binding.
          </p>
        </section>

        <section class="section" id="termination">
          <h2>9. Termination</h2>
          <p>
            We reserve the right to suspend or terminate your access to the
            Litmus Learn Materials at our sole discretion if you fail to comply
            with these Terms. You may terminate these Terms by ceasing to use
            our services.
          </p>
        </section>

        <section class="section" id="amendments">
          <h2>10. Amendments</h2>
          <p>
            We may modify these Terms from time to time to reflect changes in
            our practices or for other operational, legal, or regulatory
            reasons. Your continued use of the services after any changes
            constitutes your acceptance of the new Terms.
          </p>
        </section>

        <section class="section" id="contact">
          <h2>11. Contact Information</h2>
          <p>
            If you have any questions or concerns about these Terms, please
            contact us at:
          </p>
          <p>
            <strong>Email:</strong>
            <a href="mailto:contact@litmuslearn.com">contact@litmuslearn.com</a>
          </p>
          <p>
            <strong>Address:</strong> Dhrona EdTech Private Ltd, 1st Floor
            Hanush, 5/3 Vishwanathan Street, West Mambalam, Chennai, 600033,
            Tamil Nadu, India.
          </p>
          <p>
            In accordance with the Information Technology (Intermediary
            Guidelines and Digital Media Ethics Code) Rules, 2021, the Grievance
            Officer and Data Protection Officer is:
          </p>
          <p>
            <strong>Mr. Murali</strong> <br />
            1st Floor Hanush, 5/3 Vishwanathan Street, West Mambalam, Chennai,
            600033, Tamil Nadu, India. <br />
            <strong>Email:</strong>
            <a href="mailto:contact@litmuslearn.com">contact@litmuslearn.com</a>
          </p>
        </section>

        <section class="section" id="phet-simulations">
          <h2>12. PhET Simulations</h2>
          <p>
            Certain simulations available on our platform are provided by the
            University of Colorado Boulder as open educational resources under
            the Creative Commons Attribution (CC-BY) license. When using these
            simulations, please attribute them as follows:
          </p>
          <p>
            <em
              >PhET Interactive Simulations<br />
              University of Colorado Boulder<br />
              https://phet.colorado.edu</em
            >
          </p>
        </section>
      </div>
    </div>
  `,
  styles: [
    `
      .terms-container {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 1rem;
        background-color: #f5f5f5;
        min-height: 100vh;
      }
      .terms-card {
        background: #ffffff;
        max-width: 800px;
        width: 100%;
        padding: 2rem;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        overflow-y: auto;
        max-height: 90vh;
      }
      h1,
      h2 {
        color: #333;
        margin-top: 0;
      }
      h1 {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      h2 {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
        border-bottom: 1px solid #ddd;
        padding-bottom: 0.3rem;
      }
      p,
      li {
        font-size: 1rem;
        line-height: 1.6;
        color: #555;
        margin-bottom: 1rem;
      }
      ul {
        margin: 0 0 1rem 1.5rem;
      }
      .section {
        margin-bottom: 2rem;
      }
      a {
        color: #1a73e8;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsComponent {}
